import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation.js';
import DeleteConfirmationModal from 'components/delete-confirmation-modal/index.jsx';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import hiCellLogo from 'assets/hiCellLogo.png';
import { Creators as SpecialistCreators } from 'store/specialists/specialists-duck.js';
import { resolveImageUrl } from 'utils/apiOrigin.js';
import { formatPhoneLabel } from 'utils/specialists.js';
import SpecialistModal from './modal';

const SpecialistCardContent = ({ specialist, onEdit, onDelete, dragHandle }) => {
  const imageSrc = resolveImageUrl(specialist.imageLink) || hiCellLogo;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: specialist.active ? 'rgba(146, 178, 31, 0.35)' : 'divider',
        boxShadow: specialist.active
          ? '0 14px 34px rgba(146, 178, 31, 0.12)'
          : '0 10px 24px rgba(15, 23, 42, 0.08)',
        opacity: specialist.active ? 1 : 0.72,
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              label={`Ordem ${specialist.order}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              size="small"
              label={specialist.active ? 'Ativo' : 'Inativo'}
              color={specialist.active ? 'success' : 'default'}
            />
          </Stack>
          {dragHandle}
        </Box>

        <Avatar
          src={imageSrc}
          alt={specialist.name}
          sx={{
            width: 180,
            height: 180,
            border: '3px solid',
            borderColor: 'primary.main',
            bgcolor: 'common.white',
          }}
        >
          {specialist.name?.[0] || 'S'}
        </Avatar>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {specialist.name || 'Especialista sem nome'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatPhoneLabel(specialist.phone)}
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {specialist.active ? 'Arraste para alterar a ordem' : 'Inativos nao aparecem na home'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(specialist)}>
            Editar
          </Button>
          <IconButton
            color="error"
            aria-label={`Excluir ${specialist.name}`}
            onClick={() => onDelete(specialist)}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      </CardActions>
    </Card>
  );
};

const SortableSpecialistCard = ({ specialist, onEdit, onDelete }) => {
  const sortableId = String(specialist.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
      }}
    >
      <SpecialistCardContent
        specialist={specialist}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandle={
          <IconButton
            size="small"
            color="primary"
            {...attributes}
            {...listeners}
            aria-label={`Arrastar ${specialist.name}`}
          >
            <DragIndicatorIcon />
          </IconButton>
        }
      />
    </Box>
  );
};

const SpecialistsManagementPage = ({
  items,
  specialistsToUpdateOrder,
  specialistsFetchAsync,
  specialistsOpenModal,
  specialistsUpdateOrder,
  specialistsUpdateOrdersAsync,
  specialistsDeleteAsync,
}) => {
  const {
    isOpen,
    message,
    open: openDeleteModal,
    close: closeDeleteModal,
    confirm: confirmDelete,
  } = useDeleteConfirmation();

  const handleDelete = (specialist) => {
    openDeleteModal(
      () => specialistsDeleteAsync(specialist.id),
      `Deseja excluir o especialista ${specialist.name}?`,
    );
  };

  const fetchSpecialists = useCallback(() => {
    specialistsFetchAsync();
  }, [specialistsFetchAsync]);

  useEffect(() => {
    fetchSpecialists();
  }, [fetchSpecialists]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const activeItems = items.filter((item) => item.active);
  const inactiveItems = items.filter((item) => !item.active);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = activeItems.findIndex((item) => String(item.id) === active.id);
    const newIndex = activeItems.findIndex((item) => String(item.id) === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    specialistsUpdateOrder(oldIndex, newIndex);
  };

  return (
    <>
      <SpecialistModal />
      <DeleteConfirmationModal
        isOpen={isOpen}
        message={message}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Gestao de Especialistas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cadastre, edite e reorganize os especialistas exibidos na home.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              specialistsOpenModal({
                id: null,
                name: '',
                phone: '',
                imageLink: '',
                active: true,
              })
            }
          >
            Novo especialista
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={specialistsUpdateOrdersAsync}
            disabled={!specialistsToUpdateOrder.length}
          >
            Salvar ordem
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Chip color="success" label={`${activeItems.length} ativos`} />
        <Chip variant="outlined" label={`${inactiveItems.length} inativos`} />
      </Stack>

      {!items.length ? (
        <Box
          sx={{
            py: 8,
            px: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'rgba(146, 178, 31, 0.03)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Nenhum especialista cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Crie o primeiro card para ele aparecer na home e no WhatsApp.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              specialistsOpenModal({
                id: null,
                name: '',
                phone: '',
                imageLink: '',
                active: true,
              })
            }
          >
            Criar especialista
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Especialistas ativos
          </Typography>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeItems.map((item) => String(item.id))}
              strategy={rectSortingStrategy}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 3,
                }}
              >
                {activeItems.map((specialist) => (
                  <SortableSpecialistCard
                    key={specialist.id}
                    specialist={specialist}
                    onEdit={specialistsOpenModal}
                    onDelete={handleDelete}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>

          {!!inactiveItems.length && (
            <>
              <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
                Especialistas inativos
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 3,
                }}
              >
                {inactiveItems.map((specialist) => (
                  <SpecialistCardContent
                    key={specialist.id}
                    specialist={specialist}
                    onEdit={specialistsOpenModal}
                    onDelete={handleDelete}
                  />
                ))}
              </Box>
            </>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  items: state.Specialist.items,
  specialistsToUpdateOrder: state.Specialist.specialistsToUpdateOrder,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(SpecialistCreators, dispatch);

SpecialistCardContent.propTypes = {
  specialist: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  dragHandle: PropTypes.node,
};

SortableSpecialistCard.propTypes = {
  specialist: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

SpecialistsManagementPage.propTypes = {
  items: PropTypes.array.isRequired,
  specialistsToUpdateOrder: PropTypes.array.isRequired,
  specialistsFetchAsync: PropTypes.func.isRequired,
  specialistsOpenModal: PropTypes.func.isRequired,
  specialistsUpdateOrder: PropTypes.func.isRequired,
  specialistsUpdateOrdersAsync: PropTypes.func.isRequired,
  specialistsDeleteAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(SpecialistsManagementPage);
